"use client";

import Link from "next/link";
import { LandingNav } from "@/components/landing-nav";
import { LeadForm, type LeadField } from "@/components/lead-form";
import { PUBLIC_EMAIL } from "@/lib/contact";
import { useT } from "@/i18n/provider";

export default function ContactPage() {
  // Follows the visitor's locale via the root LocaleProvider — the contact
  // copy is fully translated (contact.* keys exist in every locale), so nav,
  // form labels and body all render in the same language.
  return <ContactPageBody />;
}

function ContactPageBody() {
  const t = useT();

  const fields: LeadField[] = [
    { name: "name", label: t("contact.fields.name"), required: true, placeholder: t("contact.fields.namePlaceholder") },
    {
      name: "email",
      label: t("contact.fields.email"),
      type: "email",
      required: true,
      placeholder: t("contact.fields.emailPlaceholder"),
    },
    {
      name: "company",
      label: t("contact.fields.company"),
      placeholder: t("contact.fields.companyPlaceholder"),
    },
    {
      name: "message",
      label: t("contact.fields.message"),
      type: "textarea",
      required: true,
      rows: 6,
      placeholder: t("contact.fields.messagePlaceholder"),
    },
  ];

  return (
    <div className="min-h-screen bg-bg-base text-fg">
      <LandingNav />
      <main className="relative">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 -top-40 h-[500px] bg-[radial-gradient(ellipse_at_top,rgba(244,63,142,0.15),transparent_60%)]"
        />
        <div className="relative mx-auto max-w-5xl px-6 pt-16 pb-24 grid md:grid-cols-[1fr_1.1fr] gap-12 items-start">
          {/* Left — pitch */}
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-brand-500/25 bg-brand-500/5 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.18em] text-brand-300">
              <span className="h-1.5 w-1.5 rounded-full bg-brand-300" />
              {t("contact.eyebrow")}
            </span>
            <h1 className="mt-5 text-balance text-4xl md:text-5xl font-semibold tracking-tight text-fg-bright leading-[1.1]">
              {t("contact.titlePart1")}{" "}
              <span className="bg-brand-grad bg-clip-text text-transparent">
                {t("contact.titleHighlight")}
              </span>
            </h1>
            <p className="mt-5 text-[15px] text-fg-dim leading-relaxed">
              {t("contact.body")}
            </p>

            <div className="mt-8 space-y-4 text-[13px] text-fg-dim">
              <div>
                <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-fg-muted">
                  {t("contact.walkthroughLabel")}
                </div>
                <Link
                  href="/demo"
                  className="mt-1 inline-flex items-center gap-1 text-brand-300 hover:text-brand-200"
                >
                  {t("contact.walkthroughCta")}
                </Link>
              </div>
              <div>
                <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-fg-muted">
                  {t("contact.emailLabel")}
                </div>
                <a
                  href={`mailto:${PUBLIC_EMAIL}`}
                  className="mt-1 inline-block text-brand-300 hover:text-brand-200"
                >
                  {PUBLIC_EMAIL}
                </a>
              </div>
            </div>

            {/* Trust copy — governed, human-approved; deliberately not overclaiming.
                The legal/company details render server-side in layout.tsx
                (ContactCompanyDetails) so they're crawler-visible without JS. */}
            <p className="mt-8 text-[13px] text-fg-muted leading-relaxed">
              {t("contact.trust")}
            </p>
            {/* Who actually replies + procurement expectations — direct access
                to the product team, stated plainly so buyers know what happens
                after they write. */}
            <p className="mt-3 text-[13px] text-fg-muted leading-relaxed">
              {t("contact.whoReplies")}
            </p>
          </div>

          {/* Right — form */}
          <div>
            <LeadForm
              kind="contact"
              fields={fields}
              submitLabel={t("contact.submitLabel")}
              successTitle={t("contact.successTitle")}
              successBody={t("contact.successBody")}
            />
          </div>
        </div>
      </main>
    </div>
  );
}
